import { Card, CardHeader, CardTitle, CardContent, Button } from '@airclou/ui';
import { Alert as AlertType } from '../../api/overview';
import { AlertTriangle, AlertCircle, Info, ArrowRight } from 'lucide-react';
import { cn } from '@airclou/ui';
import { useTranslation } from 'react-i18next';

const alertIcons = {
  warning: AlertTriangle,
  error: AlertCircle,
  info: Info,
};

const alertStyles = {
  warning: 'bg-yellow-50 dark:bg-yellow-900/20 border-yellow-200 dark:border-yellow-800',
  error: 'bg-red-50 dark:bg-red-900/20 border-red-200 dark:border-red-800',
  info: 'bg-blue-50 dark:bg-blue-900/20 border-blue-200 dark:border-blue-800',
};

const iconStyles = {
  warning: 'text-yellow-600 dark:text-yellow-400',
  error: 'text-red-600 dark:text-red-400',
  // eslint-disable-next-line no-restricted-syntax -- severity color map: info means blue, not brand
  info: 'text-blue-600 dark:text-blue-400',
};

export interface AlertsPanelProps {
  alerts: AlertType[];
  onAlertAction?: (alert: AlertType) => void;
}

export function AlertsPanel({ alerts, onAlertAction }: AlertsPanelProps) {
  const { t } = useTranslation('dashboard');

  if (alerts.length === 0) {
    return null;
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-lg">{t('alertsPanel.title')}</CardTitle>
      </CardHeader>
      <CardContent>
        <div className="space-y-3">
          {alerts.map((alert) => {
            const Icon = alertIcons[alert.type];
            return (
              <div
                key={alert.id}
                className={cn(
                  'p-4 rounded-lg border',
                  alertStyles[alert.type]
                )}
              >
                <div className="flex items-start gap-3">
                  <Icon className={cn('h-5 w-5 mt-0.5 flex-shrink-0', iconStyles[alert.type])} />
                  <div className="flex-1 min-w-0">
                    <h4 className="text-sm font-semibold text-foreground">
                      {alert.title}
                    </h4>
                    <p className="text-sm text-gray-700 dark:text-gray-300 mt-1">
                      {alert.description}
                    </p>
                    {alert.actionLabel && (
                      <Button
                        variant="link"
                        size="sm"
                        className="mt-2 p-0 h-auto"
                        onClick={() => onAlertAction?.(alert)}
                      >
                        {alert.actionLabel}
                        <ArrowRight className="ml-1 h-3 w-3" />
                      </Button>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </CardContent>
    </Card>
  );
}
